import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, cents, day, points, useCurrentCompany, useMe } from '../../lib'
import { ErrorBox, Loading, NoCompany } from '../../app/ui'
import { PageBand } from '../../app/PageBand'
import { startBankSetup } from './bankSetup'
import type { PartnerSetting, Recipient, RedeemSettings } from './types'

/** "50,000" / "50000" → 50000; "" → null (no limit); anything else → undefined. No float math. */
function parseLimit(s: string): number | null | undefined {
  const t = s.trim().replace(/,/g, '')
  if (t === '') return null
  return /^[1-9]\d{0,11}$/.test(t) ? Number(t) : undefined
}

function Row({ companyId, p, canChange }: { companyId: string; p: PartnerSetting; canChange: boolean }) {
  const qc = useQueryClient()
  const [limit, setLimit] = useState(p.limit_points === null ? '' : String(p.limit_points))
  const parsed = parseLimit(limit)
  const save = useMutation({
    mutationFn: (body: { on: boolean; limit_points: number | null }) =>
      api<PartnerSetting>('PUT', `/api/companies/${companyId}/redeem/settings/${p.partner_id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['redeem'] }),
  })
  const changed = parsed !== undefined && parsed !== p.limit_points
  return (
    <div className="panel stack" data-testid="redeem-partner" data-partner={p.partner_id}>
      <div className="row">
        <strong>{p.partner_name}</strong>
        <span className="spacer" />
        <label className="switch">
          <input
            type="checkbox"
            role="switch"
            aria-label={`Use points at ${p.partner_name}`}
            data-testid={`redeem-switch-${p.partner_id}`}
            checked={p.on}
            disabled={!canChange || save.isPending}
            onChange={(e) => save.mutate({ on: e.target.checked, limit_points: e.target.checked ? parsed ?? null : null })}
          />
          <span className="track" />
          Use points here
        </label>
      </div>
      {p.on && (
        <div className="stack">
          <div className="row">
            <label>
              Most points to use (optional){' '}
              <input
                data-testid={`redeem-limit-${p.partner_id}`}
                inputMode="numeric"
                placeholder="No limit"
                value={limit}
                disabled={!canChange}
                onChange={(e) => setLimit(e.target.value)}
              />
            </label>
            {canChange && (
              <button
                className="button"
                data-testid={`redeem-limit-save-${p.partner_id}`}
                disabled={!changed || save.isPending}
                onClick={() => save.mutate({ on: true, limit_points: parsed ?? null })}
              >
                Save
              </button>
            )}
          </div>
          {parsed === undefined && <p className="negative">Use a whole number, like 50,000.</p>}
          <dl className="grid">
            <div><dt className="muted">On since</dt><dd>{p.started_at ? day(p.started_at) : ''}</dd></div>
            <div><dt className="muted">Spend counted</dt><dd className="num" data-testid={`redeem-counted-${p.partner_id}`}>{cents(p.counted_cents)}</dd></div>
            <div><dt className="muted">Points used</dt>
              <dd className="num" data-testid={`redeem-used-${p.partner_id}`}>
                {points(p.used_points)}{p.limit_points !== null ? ` of ${points(p.limit_points)}` : ''}
              </dd></div>
          </dl>
        </div>
      )}
      {save.error && <ErrorBox error={save.error} />}
    </div>
  )
}

export function RedeemPage() {
  const company = useCurrentCompany()
  const { me } = useMe()
  const settings = useQuery({
    queryKey: ['redeem', 'settings', company?.id],
    queryFn: () => api<RedeemSettings>('GET', `/api/companies/${company!.id}/redeem/settings`),
    enabled: !!company,
  })
  const recipient = useQuery({
    queryKey: ['redeem', 'recipient', company?.id],
    queryFn: () => api<Recipient>('GET', `/api/companies/${company!.id}/payout-recipient`),
    enabled: !!company,
  })
  const bank = useMutation({
    mutationFn: () => api<{ url: string }>('POST', `/api/companies/${company!.id}/payout-recipient/link`),
    onSuccess: (r) => {
      startBankSetup(company!.id)
      window.location.assign(r.url)
    },
  })

  if (!company) return <NoCompany />
  const s = settings.data
  const r = recipient.data
  return (
    <div className="page stack">
      {/* From Ploy's prototype: the band leads with what's ready and what it's worth, then the one condition. */}
      <PageBand
        title={s ? <><span className="num" data-testid="redeem-ready">{points(s.ready_points)}</span> points ready</> : 'Redeem points'}
        sub={<>{s ? `Worth ${cents(s.ready_cents)}. ` : ''}<span data-testid="redeem-line">Points pay back what you spend at these partners. We pay out monthly.</span></>}
        fine="100 points = $1."
      />
      {company.role !== 'admin' && (
        <div className="panel" data-testid="redeem-member-note">Only your company's admins can change these.</div>
      )}
      {settings.isLoading && <Loading />}
      {settings.error && <ErrorBox error={settings.error} />}
      {s && s.partners.length === 0 && (
        <div className="panel" data-testid="redeem-none">
          No partners yet. A partner shows here once your account there is linked. <Link to="/directory">See partners</Link>
        </div>
      )}
      {s?.partners.map((p) => (
        <Row key={p.partner_id} companyId={company.id} p={p} canChange={s.can_change} />
      ))}
      {s && (
        <div className="panel stack">
          <dl className="grid">
            <div><dt className="muted">Next payout</dt><dd data-testid="redeem-next">{day(s.next_payout)}</dd></div>
          </dl>
          {s.owed_points > 0 && (
            <p data-testid="redeem-owed">A refund took back {points(s.owed_points)} points. New points pay this first.</p>
          )}
          <p className="muted">We pay a partner's share when it is at least {cents(s.minimum_cents)}. A smaller share waits for next month.</p>
          {r?.status === 'ready' && (
            <p data-testid="redeem-bank">Paid to {r.bank_name} ending {r.bank_last4}</p>
          )}
          {r?.status === 'manual' && (
            <p className="muted" data-testid="redeem-bank-manual">We pay by bank transfer and email you for your bank details.</p>
          )}
          {r && !['ready', 'manual'].includes(r.status) && (
            <div className="row">
              <p>Add your bank so we can pay you.</p>
              {s.can_change && (
                <button className="button primary" data-testid="redeem-add-bank" disabled={bank.isPending} onClick={() => bank.mutate()}>
                  Add bank
                </button>
              )}
            </div>
          )}
          {bank.error && <ErrorBox error={bank.error} />}
          <p><Link to="/redemptions" data-testid="redeem-history">Past payouts</Link></p>
        </div>
      )}
      {me?.features?.travel && <div className="e-panel stack" data-testid="redeem-travel">
        <h2 className="e-heading">Book travel</h2>
        <p>Points can also pay for business flights and hotels that ernd books by hand, at 100 points = $1.</p>
        <div><Link className="e-btn" to="/travel" data-testid="redeem-travel-open">Book travel</Link></div>
      </div>}
    </div>
  )
}
